import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import { Calendar } from 'lucide-react';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import ActionButtonOverlay from '../../components/navigation/ActionButtonOverlay';
import TimelineEntry from './components/TimelineEntry';
import FilterControls from './components/FilterControls';
import EmptyState from './components/EmptyState';
import ExportSection from './components/ExportSection';
import FirstCheckInBanner from '../../components/FirstCheckInBanner';

import CalendarView from './components/CalendarView';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import UpgradeCTA from '../../components/UpgradeCTA';
import subscriptionService from '../../services/subscriptionService';
import { symptomService } from '../../services/symptomService';
import OnboardingFlowController from '../../components/navigation/OnboardingFlowController';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';




const SymptomHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'calendar'
  const [filters, setFilters] = useState({
    dateRange: 'all',
    symptomType: 'all',
    severity: 'all',
    userType: 'all'
  });

  const [allEntries, setAllEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [expandedEntries, setExpandedEntries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [managedProfiles, setManagedProfiles] = useState([]);
  const [selectedDependent, setSelectedDependent] = useState('all');

  // Fetch symptom entries from database
  useEffect(() => {
    const fetchEntries = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await symptomService?.getEntries();

      if (fetchError) {
        setError('Failed to load symptom history');
        setAllEntries([]);
      } else {
        // Transform database entries to match component format
        const transformedEntries = (data || [])?.map(entry => ({
          id: entry?.id,
          date: new Date(entry?.created_at),
          symptomType: entry?.symptom_categories?.join(', ') || 'General Symptom',
          severity: entry?.severity || 5,
          location: entry?.body_locations?.join(', ') || 'Not specified',
          description: entry?.notes || 'No description provided',
          onset: entry?.timing || 'Not specified',
          triggers: entry?.triggers || [],
          dailyImpact: entry?.daily_impacts || [],
          medication: entry?.medication_status || 'Not specified',
          userType: 'General Adult', // Can be enhanced with user profile data
          feelingLevel: entry?.feeling_level,
          voiceNote: entry?.voice_note
        }));
        setAllEntries(transformedEntries);
      }

      setLoading(false);
    };

    fetchEntries();
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [filters, allEntries]);

  const applyFilters = () => {
    let filtered = [...allEntries];

    if (filters?.dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered?.filter(entry => {
        const entryDate = new Date(entry.date);
        const entryDay = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
        
        switch (filters?.dateRange) {
          case 'today':
            return entryDay?.getTime() === today?.getTime();
          case 'week':
            const weekAgo = new Date(today);
            weekAgo?.setDate(weekAgo?.getDate() - 7);
            return entryDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo?.setMonth(monthAgo?.getMonth() - 1);
            return entryDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    if (filters?.symptomType !== 'all') {
      filtered = filtered?.filter(entry => entry?.symptomType?.toLowerCase()?.includes(filters?.symptomType?.toLowerCase()));
    }

    if (filters?.severity !== 'all') {
      const severityRanges = {
        'mild': [1, 3],
        'moderate': [4, 6],
        'severe': [7, 10]
      };
      const [min, max] = severityRanges?.[filters?.severity] || [0, 10];
      filtered = filtered?.filter(entry => entry?.severity >= min && entry?.severity <= max);
    }

    if (filters?.userType !== 'all') {
      filtered = filtered?.filter(entry => entry?.userType === filters?.userType);
    }

    setFilteredEntries(filtered);
  };

  const toggleEntryExpansion = (entryId) => {
    setExpandedEntries(prev => ({
      ...prev,
      [entryId]: !prev?.[entryId]
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: 'all',
      symptomType: 'all',
      severity: 'all',
      userType: 'all'
    });
  };

  const hasActiveFilters = filters?.dateRange !== 'all' || filters?.symptomType !== 'all' || 
                           filters?.severity !== 'all' || filters?.userType !== 'all';

  const [subscription, setSubscription] = useState(null);
  const [trialStatus, setTrialStatus] = useState({ inTrial: false, daysRemaining: 0 });

  // Load subscription data for contextual CTAs
  useEffect(() => {
    const loadSubscriptionData = async () => {
      const { data } = await subscriptionService?.getCurrentSubscription();
      const trialInfo = await subscriptionService?.isUserInTrial();
      setSubscription(data);
      setTrialStatus(trialInfo);
    };
    loadSubscriptionData();
  }, []);

  const isBasicPlan = subscription?.plan?.planType === 'basic';
  const historyLimit = subscription?.plan?.limits?.symptom_history_days || 7;
  const hasLimitedHistory = historyLimit < 180;

  return (
    <>
      <Helmet>
        <title>Symptom History - MyBodyToday</title>
        <meta name="description" content="View and analyze your symptom history timeline with MyBodyToday" />
      </Helmet>

      <OnboardingFlowController />

      <div className="min-h-screen bg-background pb-20">
        <UserProfileHeader />
        
        {/* First Check-In Banner */}
        <FirstCheckInBanner />

        {/* Header with Close Button */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-2.5 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="Calendar" className="w-6 h-6 text-indigo-600" />
                <h1 className="text-base sm:text-lg md:text-xl font-heading font-bold text-foreground">Symptom History</h1>
              </div>
              <div className="flex items-center gap-2">
                <CloseButton navigateTo="/dashboard" ariaLabel="Close symptom history" onClose={() => navigate('/dashboard')} />
                <LogoutButton />
              </div>
            </div>
          </div>
        </div>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                Your Symptom History
              </h2>
              <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'timeline' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="List" size={16} className="inline mr-1" />
                  Timeline
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'calendar' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Calendar" size={16} className="inline mr-1" />
                  Calendar
                </button>
              </div>
            </div>
            <p className="text-sm md:text-base text-muted-foreground">
              Track patterns and review your symptom entries over time
            </p>

            {/* Dependent Filter for Caregivers */}
            {managedProfiles?.length > 0 && (
              <div className="flex items-center gap-2 mt-4 sm:mt-0">
                <Icon name="Filter" size={18} className="text-muted-foreground" strokeWidth={2} />
                <select
                  value={selectedDependent}
                  onChange={(e) => {
                    setSelectedDependent(e?.target?.value);
                    applyFilters();
                  }}
                  className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                >
                  <option value="all">All Entries</option>
                  <option value="self">My Entries</option>
                  {managedProfiles?.map((profile) => (
                    <option key={profile?.id} value={profile?.id}>
                      {profile?.name} ({profile?.relationship})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <FilterControls 
            filters={filters} 
            onFilterChange={setFilters}
            onClearFilters={handleClearFilters}
            totalEntries={allEntries?.length}
            filteredCount={filteredEntries?.length}
          />

          {/* Contextual Upgrade CTA - Limited History */}
          {isBasicPlan && hasLimitedHistory && filteredEntries?.length > 0 && (
            <div className="mb-6">
              <UpgradeCTA
                variant="compact"
                context="feature-locked"
                className=""
              />
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
              <p className="text-destructive font-medium">{error}</p>
            </div>
          ) : viewMode === 'timeline' ? (
            <div className="space-y-6">
              {filteredEntries?.length === 0 ? (
                <EmptyState hasFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
              ) : (
                <div className="space-y-4">
                  {filteredEntries?.map((entry) => (
                    <TimelineEntry
                      key={entry?.id}
                      entry={entry}
                      isExpanded={expandedEntries?.[entry?.id]}
                      onToggleExpand={() => toggleEntryExpansion(entry?.id)}
                      onExpand={() => toggleEntryExpansion(entry?.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <CalendarView entries={filteredEntries} />
          )}

          <ExportSection entries={filteredEntries} entriesCount={filteredEntries?.length} />
        </main>
      </div>
      <BottomTabNavigation />
      <ActionButtonOverlay />
    </>
  );
};

export default SymptomHistory;